import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  type OnChanges,
  type OnDestroy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type { CreateEduExpensePurpose } from '../../../shared/models/api/edu-expense-purpose.model';
import { BudgetType } from '../../../shared/models/api/edu-expense.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { EduExpensePurposeService } from '../../../shared/services/api/edu-expense-purpose.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import { budgetTypeTranslation } from '../../edu-expense-create/edu-expense-create.helpers';
import type { CreateEduExpensePurposeFormValue } from './edu-expense-purpose-create.models';

@Component({
  selector: 'opa-edu-expense-purpose-create',
  imports: [
    CommonModule,
    PorscheDesignSystemModule,
    ReactiveFormsModule,
    LetDirective,
    TranslocoPipe,
  ],
  templateUrl: './edu-expense-purpose-create.component.html',
  styleUrl: './edu-expense-purpose-create.component.scss',
})
export class EduExpensePurposeCreateComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly eduExpensePurposeService = inject(EduExpensePurposeService);
  private readonly toastManager = inject(ToastManager);
  readonly formId = inject(FormIdService).getId();

  readonly budgetTypes = Object.values(BudgetType);
  readonly budgetTypeTranslation = budgetTypeTranslation;

  @Input({ required: true }) open = false;
  @Output() dismiss = new EventEmitter<boolean | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnChanges(changes: NgChanges<EduExpensePurposeCreateComponent>): void {
    if (changes.open && this.open) this.form.reset();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(success?: boolean): void {
    this.dismiss.emit(success);
  }

  submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const purpose = this.formValueToPurpose(formValue);
    this.subSink.sink = this.eduExpensePurposeService
      .createEduExpensePurpose(purpose)
      .pipe(
        tap({
          next: (createPurpose) => {
            this.toastManager.addMessage({
              text: `Purpose '${createPurpose.description}' created`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(true);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<CreateEduExpensePurposeFormValue>> {
    const form = this.formBuilder.group<ControlsOf<CreateEduExpensePurposeFormValue>>({
      budgetType: this.formBuilder.control(null, OpaValidators.required()),
      name: this.formBuilder.control(null, [
        OpaValidators.required(),
        OpaValidators.isNormalized(),
      ]),
      description: this.formBuilder.control(null, OpaValidators.required()),
    });

    return form;
  }

  private formValueToPurpose(form: CreateEduExpensePurposeFormValue): CreateEduExpensePurpose {
    const purpose: CreateEduExpensePurpose = {
      description: form.description!,
      budgetType: form.budgetType!,
      name: form.name!,
    };
    return purpose;
  }
}
